import { useState } from "react";
import { dropshipProducts, DropshipProduct } from "@/mocks/dropship";
import OrderModal, { PlacedOrder } from "./OrderModal";
import MessageModal from "./MessageModal";

interface User { name: string; email: string; role: string; phone?: string; }
interface Props { user: User; onLogout: () => void; }

const statusColors: Record<string, string> = {
  Delivered: "bg-green-100 text-green-600",
  Shipped: "bg-sky-100 text-sky-600",
  Processing: "bg-amber-100 text-amber-600",
  Pending: "bg-gray-100 text-gray-500",
};

const statusIcons: Record<string, string> = {
  Delivered: "ri-checkbox-circle-fill",
  Shipped: "ri-truck-line",
  Processing: "ri-loader-4-line",
  Pending: "ri-time-line",
};

const initialOrders: PlacedOrder[] = [
  {
    id: "#DS-8821",
    productId: 1,
    productName: "Wireless Earbuds Pro X200",
    productImage: "https://public.readdy.ai/ai/img_res/974d7681f1bc9bd15842bdd98ca03739.jpg",
    sellerName: "Guangzhou Audio Electronics",
    qty: 2,
    unitPrice: 29.99,
    shippingFee: 9.99,
    total: 69.97,
    status: "Delivered",
    trackingNumber: "1ZXYZ4821A0012345",
    warehouseAddress: "🇨🇳 China → Los Angeles, CA Warehouse",
    deliveryAddress: "456 Oak Ave, Los Angeles, CA 90001",
    date: "Apr 8, 2026",
    confirmed: false,
  },
  {
    id: "#DS-8818",
    productId: 5,
    productName: "Collagen Face Cream 50ml",
    productImage: "https://readdy.ai/api/search-image?query=collagen%20face%20cream%2050ml%20jar%20skincare%20anti%20aging%20on%20clean%20white%20background%20professional%20product%20photography%20beauty%20cosmetics%20premium&width=400&height=300&seq=ds5&orientation=landscape",
    sellerName: "Guangzhou Beauty OEM",
    qty: 1,
    unitPrice: 24.99,
    shippingFee: 8.99,
    total: 33.98,
    status: "Shipped",
    trackingNumber: "1ZABC9912B0067890",
    warehouseAddress: "🇨🇳 China → New York, NY Warehouse",
    deliveryAddress: "789 Pine St, New York, NY 10001",
    date: "Apr 5, 2026",
    confirmed: false,
  },
];

const categories = ["All", "Electronics", "Health & Beauty", "Home & Garden", "Sports & Fitness"];

export default function BuyerDashboard({ user, onLogout }: Props) {
  const [tab, setTab] = useState<"browse" | "orders">("browse");
  const [selectedProduct, setSelectedProduct] = useState<DropshipProduct | null>(null);
  const [myOrders, setMyOrders] = useState<PlacedOrder[]>(initialOrders);
  const [messagingOrder, setMessagingOrder] = useState<PlacedOrder | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState<"popular" | "price_asc" | "price_desc">("popular");
  const [confirmingOrderId, setConfirmingOrderId] = useState<string | null>(null);

  const handleOrderPlaced = (order: PlacedOrder) => {
    setMyOrders(prev => [order, ...prev]);
    setSelectedProduct(null);
    setTimeout(() => setTab("orders"), 2200);
  };

  const handleConfirmReceipt = (orderId: string) => {
    setMyOrders(prev => prev.map(o =>
      o.id === orderId ? { ...o, confirmed: true, status: "Delivered" } : o
    ));
    setConfirmingOrderId(null);
  };

  const filteredProducts = dropshipProducts
    .filter(p => {
      const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.seller.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCat = activeCategory === "All" || p.category === activeCategory;
      return matchSearch && matchCat;
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      return b.sold - a.sold;
    });

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-700 to-stone-900 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-user-line text-white text-xl"></i>
          </div>
          <div>
            <p className="text-white/70 text-xs">Buyer Account</p>
            <h3 className="text-white font-bold">{user.name}</h3>
            <p className="text-white/50 text-xs">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-white/70 text-xs">{myOrders.length} Orders</p>
            <p className="text-white font-semibold text-sm">${myOrders.reduce((a, o) => a + o.total, 0).toFixed(2)} spent</p>
          </div>
          <button onClick={onLogout} className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-logout-box-line"></i> Sign Out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-5">
        {([["browse", "ri-store-2-line", "Browse Products"], ["orders", "ri-shopping-bag-3-line", `My Orders (${myOrders.length})`]] as const).map(([t, icon, label]) => (
          <button key={t} onClick={() => { setTab(t); setSelectedProduct(null); }} className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${tab === t ? "border-amber-400 text-amber-500" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
            <i className={icon}></i> {label}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* Browse tab */}
        {tab === "browse" && (
          <div>
            {/* Search + filter bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search products, categories, sellers..."
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
              >
                <option value="popular">Most Popular</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>

            {/* Category filter */}
            <div className="flex gap-2 flex-wrap mb-5">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-amber-400 text-white border-amber-400"
                      : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <p className="text-xs text-gray-400 mb-4">{filteredProducts.length} products found</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-product-shop>
              {filteredProducts.map(p => (
                <div
                  key={p.id}
                  className="border border-gray-100 rounded-xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
                  onClick={() => setSelectedProduct(p)}
                >
                  <div className="w-full h-40 overflow-hidden relative">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-2 right-2 bg-white/90 rounded-full px-2 py-0.5 text-xs font-bold text-green-600">
                      {p.commission}% off
                    </div>
                  </div>
                  <div className="p-3">
                    <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full">{p.category}</span>
                    <h4 className="font-semibold text-gray-800 text-sm mt-2 mb-1 line-clamp-2">{p.name}</h4>
                    <p className="text-xs text-gray-400 mb-2">{p.sellerFlag} {p.seller}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-amber-500 font-extrabold text-base">${p.price}</span>
                      <span className="text-xs text-gray-400">{p.sold.toLocaleString()} sold</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedProduct(p); }}
                      className="w-full mt-2 bg-amber-400 hover:bg-amber-500 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shopping-cart-line mr-1"></i>Order Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders tab */}
        {tab === "orders" && (
          <div>
            <h3 className="font-bold text-gray-800 mb-4">My Orders</h3>
            {myOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="ri-shopping-bag-3-line text-4xl mb-3 block"></i>
                <p className="text-sm">No orders yet. Browse products to place your first order!</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {myOrders.map(o => (
                  <div key={o.id} className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-3 p-4 bg-gray-50">
                      <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                        <img src={o.productImage} alt={o.productName} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-amber-500 font-bold text-xs">{o.id}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${statusColors[o.status]}`}>
                            <i className={statusIcons[o.status]}></i>{o.status}
                          </span>
                          {o.confirmed && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              <i className="ri-checkbox-circle-fill mr-0.5"></i>Receipt Confirmed
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-800 truncate">{o.productName}</p>
                        <p className="text-xs text-gray-400">Qty: {o.qty} · {o.date} · ${o.total.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100">
                      {/* Tracking */}
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-map-pin-line text-amber-500 text-sm shrink-0"></i>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">{o.warehouseAddress}</p>
                          <p className="text-xs text-gray-400 truncate">{o.deliveryAddress}</p>
                        </div>
                      </div>

                      {o.trackingNumber && (
                        <div className="bg-amber-50 rounded-lg px-3 py-2 mb-3 flex items-center justify-between">
                          <div>
                            <p className="text-xs text-amber-600 font-semibold">UPS Tracking Number</p>
                            <p className="text-xs font-mono font-bold text-gray-800">{o.trackingNumber}</p>
                          </div>
                          <button
                            onClick={() => navigator.clipboard?.writeText(o.trackingNumber)}
                            className="text-amber-500 hover:text-amber-600 cursor-pointer p-1"
                            title="Copy tracking number"
                          >
                            <i className="ri-file-copy-line text-sm"></i>
                          </button>
                        </div>
                      )}

                      {/* Shipping progress */}
                      <div className="flex items-center gap-1 mb-3">
                        {["Pending", "Processing", "Shipped", "Delivered"].map((s, i) => {
                          const statusOrder = ["Pending", "Processing", "Shipped", "Delivered"];
                          const currentIdx = statusOrder.indexOf(o.status);
                          const isActive = i <= currentIdx;
                          return (
                            <div key={s} className="flex items-center flex-1">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${isActive ? "bg-amber-400" : "bg-gray-200"}`}>
                                {isActive && <i className="ri-check-line text-white text-xs"></i>}
                              </div>
                              <p className={`text-xs ml-1 ${isActive ? "text-amber-600 font-semibold" : "text-gray-400"} hidden sm:block`}>{s}</p>
                              {i < 3 && <div className={`flex-1 h-0.5 mx-1 ${i < currentIdx ? "bg-amber-400" : "bg-gray-200"}`}></div>}
                            </div>
                          );
                        })}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap">
                        {o.status === "Delivered" && !o.confirmed && (
                          <button
                            onClick={() => setConfirmingOrderId(o.id)}
                            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-checkbox-circle-line"></i> Confirm Receipt
                          </button>
                        )}
                        <button
                          onClick={() => setMessagingOrder(o)}
                          className="flex items-center gap-1.5 border border-amber-300 text-amber-600 hover:bg-amber-50 text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-message-3-line"></i> Message Seller
                        </button>
                        {o.trackingNumber && (
                          <a
                            href={`https://www.ups.com/track?tracknum=${o.trackingNumber}`}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line"></i> Track on UPS
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order modal */}
      {selectedProduct && (
        <OrderModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onOrderPlaced={handleOrderPlaced}
          isLoggedIn={true}
          onLoginRequired={() => {}}
        />
      )}

      {/* Message modal */}
      {messagingOrder && (
        <MessageModal
          productName={messagingOrder.productName}
          sellerName={messagingOrder.sellerName}
          sellerFlag="🏭"
          orderId={messagingOrder.id}
          buyerName={user.name}
          onClose={() => setMessagingOrder(null)}
        />
      )}

      {/* Confirm receipt dialog */}
      {confirmingOrderId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-7 max-w-sm w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-green-500 text-3xl"></i>
            </div>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">Confirm Receipt?</h3>
            <p className="text-gray-500 text-sm mb-6">By confirming, you acknowledge that you have received your order in good condition. This will release payment to the seller.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmingOrderId(null)}
                className="flex-1 border border-gray-200 text-gray-600 font-bold py-2.5 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmReceipt(confirmingOrderId)}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
